import immutable from "immutable"
import moment from "moment"
import * as R from "ramda"

import { get, getOr } from "common/utils/get"
import has from "common/utils/has"
import { mapToUtcMoment } from "../momentHelpers/momentHelpers"
import cuckoo from "../cuckoo/cuckoo"
import Flight, { BOARDING_PASS_AVAILABILITY_NEVER } from "../../records/Flight"
import { RESERVATION_PART_CONFIG } from "../../../booking/scenes/ReservationForm/consts/reservationConsts"
import { cuckooEvents } from "../../consts/enums"

/*
API Endpoints

(A) /check_flights
(B) /api/v0.1/users/self/bookings
(C) /flights
*/

export default function(flightObj, passengersObj, airlines) {
  // Search API format:          "vehicle_type": "train" | "aircraft"
  // Check flights | MMB format: ["vehicle"]["type"]: "train" | "aircraft"
  const vehicleType =
    get(["vehicle", "type"], flightObj) || get("vehicle_type", flightObj) || "flight"

  const flightType =
    vehicleType === "aircraft" || vehicleType === "airline" ? "flight" : vehicleType
  let departureWhere
  let arrivalWhere
  if (has("src", flightObj) && has("dst", flightObj)) {
    // (A)
    departureWhere = {
      code: flightObj.src, // STN
      name: flightObj.src_name, // London
      city: flightObj.src_city, // london_gb
      country: flightObj.src_country, // GB
      terminal: flightObj.src_terminal, // A
      station: flightObj.src_station,
      // src_id // 813
    }
    arrivalWhere = {
      code: flightObj.dst,
      name: flightObj.dst_name,
      city: flightObj.dst_city,
      country: flightObj.dst_country,
      terminal: flightObj.dst_terminal,
      station: flightObj.dst_station,
    }
  } else if (has("departure.where.code", flightObj) && has("arrival.where.code", flightObj)) {
    // (B)
    departureWhere = {
      code: flightObj.departure.where.code,
      name: flightObj.departure.where.name,
      city: flightObj.departure.where.city_id,
      country: flightObj.departure.where.country,
      terminal: flightObj.departure.where.terminal,
      station: flightObj.departure.where.station,
    }
    arrivalWhere = {
      code: flightObj.arrival.where.code,
      name: flightObj.arrival.where.name,
      city: flightObj.arrival.where.city_id,
      country: flightObj.arrival.where.country,
      terminal: flightObj.arrival.where.terminal,
      station: flightObj.arrival.where.station,
    }
  } else if (has("flyFrom", flightObj) && has("flyTo", flightObj)) {
    // (C)
    // noinspection SpellCheckingInspection
    departureWhere = {
      id: flightObj.mapIdfrom,
      code: flightObj.flyFrom,
      name: flightObj.cityFrom,
      lat: flightObj.latFrom,
      lng: flightObj.lngFrom,
      station: flightObj.stationFrom,
      // TODO: Add terminals
    }
    // noinspection SpellCheckingInspection
    arrivalWhere = {
      id: flightObj.mapIdto,
      code: flightObj.flyTo,
      name: flightObj.cityTo,
      lat: flightObj.latTo,
      lng: flightObj.lngTo,
      station: flightObj.stationTo,
      // TODO: Add terminals
    }
  } else {
    cuckoo.error(cuckooEvents.INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_LOCATION, {
      input: flightObj,
    })
  }

  let departureWhen
  let arrivalWhen
  if (has("dtime", flightObj) && has("atime", flightObj)) {
    // (A)
    departureWhen = {
      local: moment.utc(flightObj.dtime * 1000),
      utc: moment.utc(flightObj.dtime_utc * 1000),
    }
    arrivalWhen = {
      local: moment.utc(flightObj.atime * 1000),
      utc: moment.utc(flightObj.atime_utc * 1000),
    }
  } else if (has("departure.when.local", flightObj) && has("arrival.when.local", flightObj)) {
    // (B)
    departureWhen = {
      local: mapToUtcMoment(flightObj.departure.when.local),
      utc: mapToUtcMoment(flightObj.departure.when.utc),
    }
    arrivalWhen = {
      local: mapToUtcMoment(flightObj.arrival.when.local),
      utc: mapToUtcMoment(flightObj.arrival.when.utc),
    }
  } else if (has("dTime", flightObj) && has("aTime", flightObj)) {
    // (C)
    departureWhen = {
      local: moment.utc(flightObj.dTime * 1000),
      utc: moment.utc(flightObj.dTimeUTC * 1000),
    }
    arrivalWhen = {
      local: moment.utc(flightObj.aTime * 1000),
      utc: moment.utc(flightObj.aTimeUTC * 1000),
    }
  } else {
    cuckoo.error(cuckooEvents.INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_TIME, {
      input: flightObj,
    })
  }

  let airlineCode
  if (has("airline.iata", flightObj)) {
    airlineCode = flightObj.airline.iata
  } else if (typeof flightObj.airline === "string") {
    airlineCode = flightObj.airline
  } else {
    cuckoo.error(cuckooEvents.INTERNAL_MAPPER_FLIGHT_UNSUPPORTED_FORMAT_AIRLINE, {
      input: flightObj,
    })
  }

  const airline = {
    code: airlineCode,
    name: get([airlineCode, "name"], airlines),
    logo: getOr(RESERVATION_PART_CONFIG[flightType].logo, [airlineCode, "logo"], airlines),
    closeBookingHours: get(["airline", "close_booking_hours"], flightObj), // only from check_flights api call
  }

  const operatingAirline = flightObj.operating_airline
    ? immutable.fromJS({
        code: flightObj.operating_airline.iata,
        name: flightObj.operating_airline.name,
        logo: getOr(
          RESERVATION_PART_CONFIG[flightType].logo,
          [flightObj.operating_airline.iata, "logo"],
          airlines,
        ),
      })
    : null

  let boardingPassAvailableAt
  // Beware: boardingPassAvailableAt is really called ticket_available_at in API
  if (typeof flightObj.ticket_available_at !== "undefined") {
    // Information is in response
    boardingPassAvailableAt =
      flightObj.ticket_available_at === null
        ? BOARDING_PASS_AVAILABILITY_NEVER // null from api means "never"
        : flightObj.ticket_available_at
  }

  const returnFareCategory = (type, vehicle) =>
    R.isEmpty(type) || (R.isNil(type) && vehicle === "aircraft") ? "M" : type

  const priorityBoardingForEachPassenger =
    passengersObj &&
    R.values(passengersObj).every(passengerObj =>
      getOr(false, ["priority_boarding", flightObj.id, "is_ordered"], passengerObj),
    )
  return new Flight({
    id: flightObj.id,
    number: flightObj.flight_no,
    pnr: flightObj.reservation_number,
    bagsRecheckRequired: Boolean(flightObj.bags_recheck_required),
    guarantee: R.isNil(flightObj.guarantee)
      ? Boolean(flightObj.bags_recheck_required) // Fallback for some APIs (e.g. search) that doesn't return `guarantee` param yet
      : flightObj.guarantee,
    boardingPassAvailableAt,
    priorityBoarding: flightObj.forced_priority_boarding || priorityBoardingForEachPassenger, // derived from passengersObj for MMB
    isCabinBagsWithPriorityBoarding: flightObj.is_cabin_bags_with_priority_boarding || false,
    seatmap: null, // not available yet, will be returned from different API

    // TODO thing about use of Place and TimeInPlace objects
    departure: immutable.fromJS({
      where: departureWhere,
      when: departureWhen,
    }),
    arrival: immutable.fromJS({
      where: arrivalWhere,
      when: arrivalWhen,
    }),
    airline: immutable.fromJS(airline),
    vehicle: flightObj.vehicle && immutable.fromJS(flightObj.vehicle),
    operatingAirline,
    type: flightType,
    isHidden: flightObj.is_hidden || false,
    fareCategory: returnFareCategory(flightObj.fare_category, flightObj.vehicle?.type),
  })
}
